/**
 * Application Router
 *
 * Centralized route configuration with lazy loading.
 * Each module exports its own routes that are composed here.
 */

import { lazy } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { AuthGuard, GuestGuard, RoleGuard } from '@/modules/auth';
import { NotFound } from '@/shared/components';
import { APP_ROUTES } from '@/shared/constants';
import { MainLayout } from '@/shared/layouts';

// Re-export APP_ROUTES for convenience
export { APP_ROUTES } from '@/shared/constants';

// ============================================================================
// Lazy-loaded Module Pages
// ============================================================================

// Auth Module
const LoginPage = lazy(() => import('@/modules/auth').then(m => ({ default: m.LoginPage })));

const AuthCallbackPage = lazy(() => import('@/modules/auth').then(m => ({ default: m.AuthCallbackPage })));

// Queue Module
const QueueListPage = lazy(() => import('@/modules/queue').then(m => ({ default: m.QueueListPage })));

// Dashboard Module
const DashboardPage = lazy(() => import('@/modules/dashboard').then(m => ({ default: m.DashboardPage })));

// Cases Module
const CaseListPage = lazy(() => import('@/modules/cases').then(m => ({ default: m.CaseListPage })));

// Contacts Module
const ContactListPage = lazy(() => import('@/modules/contacts').then(m => ({ default: m.ContactListPage })));

// Case Detail Module
const CaseDetailPageView = lazy(() => import('@/modules/case-detail').then(m => ({ default: m.CaseDetailPage })));

// Case Tags Module
const CaseTagPage = lazy(() => import('@/modules/case-tags').then(m => ({ default: m.CaseTagPage })));

// Users Module
const UserListPage = lazy(() => import('@/modules/users').then(m => ({ default: m.UserListPage })));

// User Detail Module
const UserDetailPage = lazy(() => import('@/modules/user-detail').then(m => ({ default: m.UserDetailPage })));

// Entities Module
const EntityListPage = lazy(() => import('@/modules/entities').then(m => ({ default: m.EntityListPage })));

// Client Detail Module
const ClientDetailPage = lazy(() => import('@/modules/client-detail').then(m => ({ default: m.ClientDetailPage })));

// Payments Module
const PaymentListPage = lazy(() => import('@/modules/payments').then(m => ({ default: m.PaymentListPage })));

// Case PTP Module
const CasePtpListPage = lazy(() => import('@/modules/case-ptp').then(m => ({ default: m.CasePtpListPage })));

// Case Deferment Module
const CaseDefermentListPage = lazy(() => import('@/modules/case-deferment').then(m => ({ default: m.CaseDefermentListPage })));

// Field Visits Module
const FieldListPage = lazy(() => import('@/modules/field').then(m => ({ default: m.FieldListPage })));

// Call Follow-Up Module
const CallFollowUpListPage = lazy(() => import('@/modules/call-follow-up').then(m => ({ default: m.CallFollowUpListPage })));

// Call History Module
const CallHistoryListPage = lazy(() => import('@/modules/call-history').then(m => ({ default: m.CallHistoryListPage })));

const PhoneSearchPage = lazy(() => import('@/modules/phone-search').then(m => ({ default: m.PhoneSearchPage })));

const OAuthClientListPage = lazy(() => import('@/modules/oauth-clients').then(m => ({ default: m.OAuthClientListPage })));

const OAuthClientDetailPage = lazy(() => import('@/modules/oauth-clients').then(m => ({ default: m.OAuthClientDetailPage })));

// ============================================================================
// Router Component
// ============================================================================

function LegacyCaseDetailRedirect() {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: APP_ROUTES.CASE_DETAIL.replace(':caseId', encodeURIComponent(caseId)), search, hash }} replace />;
}

export function AppRouter() {
  return (
    <Routes>
      {/* Auth Routes (Guest Only) */}
      <Route
        path={APP_ROUTES.AUTH.ROOT.slice(1)}
        element={
          <GuestGuard>
            <LoginPage />
          </GuestGuard>
        }
      />

      {/* OAuth Callback Routes */}
      <Route path={APP_ROUTES.AUTH.CALLBACK.slice(1)} element={<AuthCallbackPage />} />
      <Route path={APP_ROUTES.AUTH.GOOGLE_CALLBACK.slice(1)} element={<AuthCallbackPage />} />

      {/* Legacy case detail URL -> canonical. Outside AuthGuard on purpose: login's `from` is then the canonical path. */}
      <Route path={APP_ROUTES.LEGACY_CASE_DETAILS.slice(1)} element={<LegacyCaseDetailRedirect />} />

      {/* Protected Routes */}
      <Route
        element={
          <AuthGuard>
            <MainLayout />
          </AuthGuard>
        }
      >
        {/* Default redirect */}
        <Route index element={<Navigate to={APP_ROUTES.DASHBOARD} replace />} />

        {/* Queue Module */}
        <Route path={APP_ROUTES.QUEUE.slice(1)} element={<QueueListPage />} />

        {/* Cases Module */}
        <Route path={APP_ROUTES.CASES.LIST.slice(1)} element={<CaseListPage />} />

        {/* Case Detail Module */}
        <Route path={APP_ROUTES.CASE_DETAIL.slice(1)} element={<CaseDetailPageView />} />

        {/* Case Tags Module */}
        <Route path={APP_ROUTES.CASE_TAGS.slice(1)} element={<CaseTagPage />} />

        {/* Users Module */}
        <Route path={APP_ROUTES.USERS.LIST.slice(1)} element={<UserListPage />} />

        {/* User Detail Module */}
        <Route path={APP_ROUTES.USERS.DETAIL.slice(1)} element={<UserDetailPage />} />

        {/* Entities Module */}
        <Route path={APP_ROUTES.ENTITIES.slice(1)} element={<EntityListPage />} />

        {/* Payments Module */}
        <Route path={APP_ROUTES.PAYMENTS.slice(1)} element={<PaymentListPage />} />

        {/* Clients Module */}
        <Route path={APP_ROUTES.CLIENTS.LIST.slice(1)} element={<ContactListPage />} />

        {/* Contacts Module */}
        <Route path={APP_ROUTES.CONTACTS.slice(1)} element={<ContactListPage />} />

        {/* Client Detail Module */}
        <Route path={APP_ROUTES.CLIENTS.DETAIL.slice(1)} element={<ClientDetailPage />} />

        {/* Case PTP Module */}
        <Route path={APP_ROUTES.PTP.slice(1)} element={<CasePtpListPage />} />

        {/* Case Deferment Module */}
        <Route path={APP_ROUTES.DEFERMENTS.slice(1)} element={<CaseDefermentListPage />} />

        {/* Call Follow-Up Module */}
        <Route path={APP_ROUTES.CALL_FOLLOW_UPS.slice(1)} element={<CallFollowUpListPage />} />

        {/* Call History Module */}
        <Route path={APP_ROUTES.CALL_HISTORY.slice(1)} element={<CallHistoryListPage />} />

        {/* Phone Search Module */}
        <Route path={APP_ROUTES.PHONE_SEARCH.slice(1)} element={<PhoneSearchPage />} />

        {/* Field Visits Module */}
        <Route path={APP_ROUTES.FIELD_VISITS.slice(1)} element={<FieldListPage />} />

        {/* OAuth Client Management */}
        <Route
          path={APP_ROUTES.OAUTH_CLIENTS.LIST.slice(1)}
          element={
            <RoleGuard>
              <OAuthClientListPage />
            </RoleGuard>
          }
        />
        <Route
          path={APP_ROUTES.OAUTH_CLIENTS.DETAIL.slice(1)}
          element={
            <RoleGuard>
              <OAuthClientDetailPage />
            </RoleGuard>
          }
        />

        {/* Dashboard Module */}
        <Route path={APP_ROUTES.DASHBOARD.slice(1)} element={<DashboardPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
